import { useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { CheckCircle, XCircle, Loader } from 'lucide-react'
import { MortiseMark } from '@/components/atoms/MortiseMark'

export default function VerifyEmailPage() {
  useEffect(() => {
    document.title = "Tenon — Verify Email"
    return () => { document.title = "Tenon" }
  }, [])

  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')

  useEffect(() => {
    const token = params.get('token')
    if (!token) {
      setStatus('error')
      setMessage('No verification token provided.')
      return
    }

    fetch(`/api/v1/auth/verify-email?token=${encodeURIComponent(token)}`, {
      redirect: 'manual',
    })
      .then(r => {
        if (r.ok || r.status === 302 || r.type === 'opaqueredirect') {
          setStatus('success')
          setMessage('Your email has been verified. Redirecting…')
          setTimeout(() => navigate('/dashboard'), 2000)
        } else {
          return r.json().then(d => { throw new Error(d.error || 'Verification failed') })
        }
      })
      .catch(e => {
        setStatus('error')
        setMessage(e.message || 'Verification failed. The link may have expired.')
      })
  }, [params, navigate])

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-page)',
      fontFamily: "'Karla', -apple-system, sans-serif",
      paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))',
      paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
      paddingLeft: '16px',
      paddingRight: '16px',
      position: 'relative',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(80% 60% at 100% 0%, color-mix(in oklch, var(--brand-gold) 10%, transparent), transparent 70%)',
      }} />

      <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>

        <a href="https://usetenon.com" style={{ display: 'flex', alignItems: 'center', marginBottom: 32, textDecoration: 'none' }}>
          <MortiseMark size={44} light />
        </a>

        <div style={{
          width: '100%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: '36px 40px',
          boxShadow: 'var(--shadow-2)',
        }}>
          {/* Eyebrow */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--brand-gold)', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--text-muted)', fontFamily: "'Karla', -apple-system, sans-serif" }}>
              Tenon · Verify Email
            </span>
          </div>

          {status === 'loading' && (
            <>
              <h1 style={{ fontSize: '1.625rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
                Verifying your email
              </h1>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, marginTop: 0, lineHeight: 1.55 }}>
                Just a moment…
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                <Loader size={32} color="var(--brand-gold)" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <h1 style={{ fontSize: '1.625rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
                Email verified
              </h1>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, marginTop: 0, lineHeight: 1.55 }}>
                {message}
              </p>
              <div style={{
                padding: '16px',
                background: 'color-mix(in oklch, var(--st-success) 8%, transparent)',
                border: '1px solid color-mix(in oklch, var(--st-success) 40%, transparent)',
                borderRadius: 10,
                color: 'var(--st-success)',
                fontSize: 14,
                lineHeight: 1.6,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                justifyContent: 'center',
              }}>
                <CheckCircle size={18} /> Verified
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <h1 style={{ fontSize: '1.625rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
                Verification failed
              </h1>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, marginTop: 0, lineHeight: 1.55 }}>
                {message}
              </p>
              <div style={{
                padding: '16px',
                background: 'color-mix(in oklch, var(--st-error) 8%, transparent)',
                border: '1px solid color-mix(in oklch, var(--st-error) 40%, transparent)',
                borderRadius: 10,
                color: 'var(--st-error)',
                fontSize: 14,
                lineHeight: 1.6,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 20,
              }}>
                <XCircle size={18} /> Link invalid or expired
              </div>
              <div style={{ textAlign: 'center' }}>
                <Link to="/login" style={{ color: 'var(--text-secondary)', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                  ← Back to log in
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Sage callout */}
        <div style={{
          marginTop: 24,
          background: 'color-mix(in oklch, var(--brand-gold) 7%, var(--bg-surface))',
          border: '1px solid color-mix(in oklch, var(--brand-gold) 18%, var(--border-subtle))',
          borderRadius: 10,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          width: '100%',
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7, flexShrink: 0,
            background: 'color-mix(in oklch, var(--brand-gold) 15%, var(--bg-elevated))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 12, color: 'var(--brand-gold)',
            fontFamily: "'Karla', -apple-system, sans-serif",
          }}>S</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>
            <strong style={{ color: 'var(--text-primary)' }}>Sage is watching your projects.</strong> Log in to see what needs your attention today.
          </p>
        </div>
      </div>
    </div>
  )
}
